import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star, CheckCircle2, AlertCircle, ImageIcon, MessageSquare, Award } from "lucide-react";
import { fileUrl } from "../lib";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper local pra fazer chamadas autenticadas (lê token do localStorage)
const authApi = (method, path, body) => {
  const token = localStorage.getItem("cps_token");
  return axios({
    method, url: `${API}${path}`, data: body,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  }).then(r => r.data);
};

/* =========================================================================
 * PUBLIC NPS FORM — /nps/:token
 * Cliente da obra recebe link por email, abre sem login e responde em 1 min.
 * ========================================================================= */
export function NPSPublicForm() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [nota, setNota] = useState(null);
  const [subQ, setSubQ] = useState(0);
  const [subP, setSubP] = useState(0);
  const [subC, setSubC] = useState(0);
  const [subI, setSubI] = useState(0);
  const [comentario, setComentario] = useState("");
  const [fotoUrl, setFotoUrl] = useState("");
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/nps/${token}`)
      .then(r => {
        if (r.data.ja_respondido) {
          setDone({ alreadyResponded: true, ...r.data });
        } else {
          setInfo(r.data);
        }
      })
      .catch(e => setErr(e.response?.data?.detail || "Link inválido ou expirado"));
  }, [token]);

  const uploadFoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { alert("Imagem muito grande (max 25MB)"); return; }
    setUploadingFoto(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/public/nps/${token}/foto`, fd);
      setFotoUrl(r.data.url);
    } catch (er) {
      alert("Erro ao enviar foto: " + (er.response?.data?.detail || er.message));
    } finally {
      setUploadingFoto(false);
    }
  };

  const submit = async () => {
    if (nota === null) { alert("Por favor escolha uma nota de 0 a 10"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/nps/${token}/respond`, {
        nota_geral: nota,
        sub_qualidade: subQ || null,
        sub_prazo: subP || null,
        sub_comunicacao: subC || null,
        sub_instalacao: subI || null,
        comentario: comentario.trim() || null,
        foto_url: fotoUrl || null,
      });
      setDone({ categoria: r.data.categoria, nota });
    } catch (er) {
      alert("Erro: " + (er.response?.data?.detail || er.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (err) {
    return (
      <div className="container" style={{ paddingTop: 120, textAlign: "center", maxWidth: 560 }}>
        <AlertCircle size={48} color="var(--gold)" style={{ margin: "0 auto 24px", display: "block" }} />
        <h2 className="page-title"><em>Link inválido</em></h2>
        <p className="page-sub" style={{ marginTop: 14 }}>{err}</p>
        <p className="page-sub" style={{ fontSize: 12, opacity: 0.7, marginTop: 20 }}>
          Se você acredita que isso é um engano, entre em contato com a CAMASA.
        </p>
      </div>
    );
  }

  if (done) {
    const isPromoter = done.categoria === "promoter" || done.alreadyResponded;
    return (
      <div className="container" style={{ paddingTop: 100, textAlign: "center", maxWidth: 640 }}>
        <CheckCircle2 size={56} color="var(--verde-2)" style={{ margin: "0 auto 24px", display: "block" }} />
        <h2 className="page-title">
          {done.alreadyResponded
            ? <>Avaliação <em>já registrada</em></>
            : isPromoter
              ? <>Obrigado, <em>de coração</em></>
              : <>Recebido. <em>Obrigado pela honestidade.</em></>}
        </h2>
        <p className="page-sub" style={{ marginTop: 18, fontSize: 16, lineHeight: 1.7 }}>
          {done.alreadyResponded
            ? "Sua resposta já foi enviada. Cada avaliação ajuda a CAMASA a manter o padrão premium em cada obra."
            : isPromoter
              ? "Sua opinião nos ajuda a manter o padrão premium em cada obra. A CAMASA valoriza profundamente a confiança que você depositou em nós."
              : "Vamos analisar com cuidado os pontos que podemos melhorar. Em breve um membro da equipe entrará em contato pessoalmente."}
        </p>
        <div style={{ marginTop: 30, display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".25em", textTransform: "uppercase", fontStyle: "italic", fontFamily: "'Fraunces',serif" }}>
            "Il vero lusso è raro come un sogno"
          </span>
        </div>
      </div>
    );
  }

  if (!info) return <div className="container" style={{ paddingTop: 120, textAlign: "center" }}><p className="page-sub">Carregando…</p></div>;

  const npsLabel = (n) => {
    if (n === null) return "Selecione uma nota";
    if (n >= 9) return "🌟 Promoter — você ama!";
    if (n >= 7) return "👍 Passive — bem servido";
    return "⚠️ Detractor — algo precisa melhorar";
  };

  return (
    <div className="container" style={{ paddingTop: 60, paddingBottom: 80, maxWidth: 720 }}>
      <div className="fade-up" style={{ textAlign: "center", marginBottom: 40 }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          {info.obra_codigo} · Avaliação Pós-Entrega
        </div>
        <h1 className="page-title" data-testid="nps-title">
          Como foi sua <em>experiência</em>?
        </h1>
        <p className="page-sub" style={{ marginTop: 14, maxWidth: 560, margin: "14px auto 0", fontSize: 15 }}>
          Olá <strong>{(info.cliente_nome || "").split(" ")[0]}</strong>, sua opinião sobre <strong>{info.obra_nome}</strong> nos ajuda a manter o padrão premium em cada projeto. Leva menos de 1 minuto.
        </p>
      </div>

      {info.imagem_capa && (
        <div style={{
          height: 220, backgroundImage: `url(${fileUrl(info.imagem_capa)})`,
          backgroundSize: "cover", backgroundPosition: "center",
          marginBottom: 36, border: "1px solid var(--gold-3)",
          position: "relative",
        }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 50%, rgba(13,13,15,0.85) 100%)" }} />
          <div style={{ position: "absolute", bottom: 16, left: 18, right: 18 }}>
            <div className="eyebrow" style={{ marginBottom: 4, color: "var(--gold)" }}>Sua obra</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 22, fontStyle: "italic", color: "var(--ink-1)" }}>{info.obra_nome}</div>
          </div>
        </div>
      )}

      {/* NPS slider 0-10 */}
      <div className="panel" style={{ padding: 24, marginBottom: 24 }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>1. Recomendação Geral</div>
        <h3 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 20, marginBottom: 6 }}>
          De 0 a 10, qual a probabilidade de você indicar a CAMASA a um amigo ou colega?
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(11, 1fr)", gap: 6, marginTop: 20 }}>
          {[...Array(11)].map((_, n) => (
            <button
              key={n}
              type="button"
              onClick={() => setNota(n)}
              data-testid={`nps-nota-${n}`}
              style={{
                aspectRatio: "1", padding: 0,
                border: nota === n ? "2px solid var(--gold)" : "1px solid var(--line)",
                background: nota === n ? "rgba(184,147,90,0.15)" : "var(--bg-3)",
                color: nota === n ? "var(--gold)" : "var(--ink-2)",
                fontFamily: "'Fraunces',serif", fontSize: 18, fontStyle: "italic",
                cursor: "pointer", transition: "all .2s",
              }}
            >{n}</button>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 10, color: "var(--ink-3)", letterSpacing: ".15em", textTransform: "uppercase" }}>
          <span>Não recomendaria</span>
          <span>Indicaria com certeza</span>
        </div>
        <div style={{ marginTop: 18, padding: "10px 14px", background: "var(--bg-3)", textAlign: "center", fontSize: 13, color: nota === null ? "var(--ink-3)" : "var(--gold)", fontStyle: "italic" }}>
          {npsLabel(nota)}
        </div>
      </div>

      {/* Sub-notas */}
      <div className="panel" style={{ padding: 24, marginBottom: 24 }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>2. Avaliação Detalhada (opcional)</div>
        <h3 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 20, marginBottom: 16 }}>
          Avalie cada dimensão de 1 a 5 estrelas
        </h3>
        {[
          { lbl: "Qualidade do material", val: subQ, set: setSubQ, k: "qualidade" },
          { lbl: "Prazo de entrega", val: subP, set: setSubP, k: "prazo" },
          { lbl: "Comunicação durante a obra", val: subC, set: setSubC, k: "comunicacao" },
          { lbl: "Instalação e acabamento", val: subI, set: setSubI, k: "instalacao" },
        ].map(({ lbl, val, set, k }) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid var(--line)" }}>
            <span style={{ fontSize: 14, color: "var(--ink-2)" }}>{lbl}</span>
            <div style={{ display: "flex", gap: 4 }}>
              {[1, 2, 3, 4, 5].map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => set(val === s ? 0 : s)}
                  data-testid={`nps-sub-${k}-${s}`}
                  style={{
                    background: "transparent", border: "none", padding: 4,
                    cursor: "pointer", color: s <= val ? "var(--gold)" : "var(--ink-3)",
                    transition: "color .15s",
                  }}
                >
                  <Star size={20} fill={s <= val ? "var(--gold)" : "transparent"} strokeWidth={1.5} />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Comentário + foto */}
      <div className="panel" style={{ padding: 24, marginBottom: 24 }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>3. Comentário (opcional)</div>
        <h3 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 20, marginBottom: 16 }}>
          O que mais marcou em sua experiência?
        </h3>
        <textarea
          rows={4}
          value={comentario}
          onChange={e => setComentario(e.target.value)}
          placeholder="Pode ser um detalhe que amou, um ponto de melhoria, ou apenas um agradecimento…"
          data-testid="nps-comentario"
          style={{ width: "100%", padding: 12, background: "var(--bg-3)", border: "1px solid var(--line)", color: "var(--ink-1)", fontFamily: "inherit", fontSize: 14, resize: "vertical" }}
        />
        <div style={{ marginTop: 18 }}>
          <label style={{ fontSize: 12, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".15em" }}>Foto do resultado final (opcional)</label>
          {fotoUrl ? (
            <div style={{ marginTop: 8, display: "flex", gap: 14, alignItems: "center" }}>
              <div style={{ width: 100, height: 70, backgroundImage: `url(${fileUrl(fotoUrl)})`, backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--gold-3)" }} />
              <button type="button" onClick={() => setFotoUrl("")} className="btn btn-ghost btn-sm" data-testid="nps-foto-remove">Remover</button>
            </div>
          ) : (
            <label style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              marginTop: 8, padding: "16px", border: "2px dashed var(--gold-3)",
              background: "rgba(184,147,90,0.04)", cursor: uploadingFoto ? "wait" : "pointer",
              fontSize: 13, color: "var(--ink-2)",
            }} data-testid="nps-foto-upload">
              <ImageIcon size={18} color="var(--gold)" />
              {uploadingFoto ? "Enviando…" : "Adicionar foto do ambiente final"}
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={uploadFoto} disabled={uploadingFoto} />
            </label>
          )}
        </div>
      </div>

      <button
        className="btn btn-primary"
        style={{ width: "100%", padding: 18, fontSize: 14, letterSpacing: ".3em" }}
        onClick={submit}
        disabled={submitting || nota === null}
        data-testid="nps-submit"
      >
        {submitting ? "Enviando…" : nota === null ? "Escolha uma nota acima" : "Enviar avaliação"}
      </button>
      <p style={{ textAlign: "center", marginTop: 16, fontSize: 11, color: "var(--ink-3)", fontStyle: "italic" }}>
        Sua avaliação é confidencial e usada apenas pela CAMASA para melhoria contínua.
      </p>
    </div>
  );
}


/* =========================================================================
 * ADMIN NPS PAGE — /nps
 * KPIs gerais + lista de respostas
 * ========================================================================= */
export function NPSAdminPage() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("todas");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        authApi("get", "/nps/summary"),
        authApi("get", "/nps/responses"),
      ]);
      setSummary(s);
      setItems(r.items || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const recheck = async () => {
    setBusy(true);
    try {
      const r = await authApi("post", "/nps/check-pending");
      alert(`Convites NPS:\n• ${r.sent_initial} enviados\n• ${r.sent_reminder} lembretes`);
      load();
    } catch (e) { alert("Erro: " + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const filtered = items.filter(x => {
    if (filter === "promoters") return x.categoria === "promoter";
    if (filter === "passives") return x.categoria === "passive";
    if (filter === "detractors") return x.categoria === "detractor";
    return true;
  });

  const npsScoreColor = (s) => {
    if (s === null) return "var(--ink-3)";
    if (s >= 70) return "var(--verde-2)";
    if (s >= 30) return "var(--gold)";
    if (s >= 0) return "#d4ac5a";
    return "#c47a7a";
  };

  const catBadge = (cat) => {
    const map = {
      promoter: { lbl: "Promoter", color: "var(--verde-2)", icon: "🌟" },
      passive: { lbl: "Passive", color: "var(--gold)", icon: "👍" },
      detractor: { lbl: "Detractor", color: "#c47a7a", icon: "⚠️" },
    };
    const m = map[cat] || { lbl: cat, color: "var(--ink-2)", icon: "" };
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", border: `1px solid ${m.color}40`, color: m.color, fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase" }}>
        {m.icon} {m.lbl}
      </span>
    );
  };

  return (
    <div className="container">
      <div className="page-head fade-up">
        <div>
          <div className="eyebrow"><span style={{ fontStyle: "italic" }}>★</span> Voz do cliente</div>
          <h1 className="page-title">NPS <em>Pós-Entrega</em></h1>
          <p className="page-sub">Avaliações dos clientes após entrega da obra. Cada nota alimenta o ranking dos terceirizados.</p>
        </div>
        <button className="btn btn-ghost" onClick={recheck} disabled={busy} data-testid="nps-recheck">
          {busy ? "Verificando…" : "Verificar pendentes"}
        </button>
      </div>

      {/* KPIs */}
      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 28 }}>
          <div className="panel" style={{ padding: 20, textAlign: "center", border: "1px solid var(--gold-3)" }} data-testid="nps-kpi-score">
            <div className="eyebrow" style={{ marginBottom: 8 }}>NPS Score</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 42, fontStyle: "italic", color: npsScoreColor(summary.nps_score) }}>
              {summary.nps_score === null ? "—" : summary.nps_score}
            </div>
            <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>
              {summary.total_respostas} respostas · {summary.pendentes || 0} pendentes
            </div>
          </div>
          <div className="panel" style={{ padding: 20, textAlign: "center" }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Nota média</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontSize: 42, fontStyle: "italic", color: "var(--gold)" }}>
              {summary.nota_media === null ? "—" : `${summary.nota_media}/10`}
            </div>
          </div>
          <div className="panel" style={{ padding: 20 }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>Distribuição</div>
            {[
              ["promoters", summary.promoters, summary.promoters_pct, "var(--verde-2)", "🌟 Promoters"],
              ["passives", summary.passives, summary.passives_pct, "var(--gold)", "👍 Passives"],
              ["detractors", summary.detractors, summary.detractors_pct, "#c47a7a", "⚠️ Detractors"],
            ].map(([k, count, pct, color, lbl]) => (
              <div key={k} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
                  <span style={{ color: "var(--ink-2)" }}>{lbl}</span>
                  <span style={{ color }}>{count} · {pct}%</span>
                </div>
                <div style={{ height: 4, background: "var(--bg-3)" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: color }} />
                </div>
              </div>
            ))}
          </div>
          <div className="panel" style={{ padding: 20 }}>
            <div className="eyebrow" style={{ marginBottom: 12 }}>Sub-notas (1-5)</div>
            {[
              ["qualidade", "Qualidade material"],
              ["prazo", "Prazo"],
              ["comunicacao", "Comunicação"],
              ["instalacao", "Instalação"],
            ].map(([k, lbl]) => {
              const v = summary.sub_medias?.[k];
              return (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", borderBottom: "1px solid var(--line)" }}>
                  <span style={{ color: "var(--ink-2)" }}>{lbl}</span>
                  <span style={{ color: "var(--gold)", fontFamily: "'Fraunces',serif", fontStyle: "italic" }}>
                    {v === null || v === undefined ? "—" : `${v}/5 ★`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          ["todas", "Todas"],
          ["promoters", "Promoters"],
          ["passives", "Passives"],
          ["detractors", "Detractors"],
        ].map(([k, lbl]) => (
          <button
            key={k}
            className={"chip " + (filter === k ? "active" : "")}
            onClick={() => setFilter(k)}
            data-testid={`nps-filter-${k}`}
          >{lbl}</button>
        ))}
      </div>

      {/* Lista respostas */}
      {filtered.length === 0 ? (
        <div className="empty"><h3>Nenhuma resposta {filter !== "todas" ? "neste filtro" : "ainda"}</h3></div>
      ) : (
        <div style={{ display: "grid", gap: 12 }} data-testid="nps-list">
          {filtered.map(r => (
            <div key={r.id} className="panel" style={{ padding: 18 }} data-testid={`nps-row-${r.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 10 }}>
                <div>
                  <div className="eyebrow" style={{ marginBottom: 4 }}>{r.obra_codigo}</div>
                  <h3 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 17, margin: 0 }}>{r.obra_nome}</h3>
                  <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>{r.cliente_nome} · {new Date(r.responded_at).toLocaleDateString("pt-BR")}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontFamily: "'Fraunces',serif", fontSize: 32, fontStyle: "italic", color: "var(--gold)", lineHeight: 1 }}>{r.nota_geral}</div>
                    <div style={{ fontSize: 10, color: "var(--ink-3)", marginTop: 2 }}>de 10</div>
                  </div>
                  {catBadge(r.categoria)}
                </div>
              </div>
              {(r.sub_qualidade || r.sub_prazo || r.sub_comunicacao || r.sub_instalacao) && (
                <div style={{ display: "flex", gap: 14, fontSize: 11, color: "var(--ink-2)", flexWrap: "wrap", marginBottom: 8 }}>
                  {r.sub_qualidade && <span>Qualidade: <span style={{ color: "var(--gold)" }}>{r.sub_qualidade}/5 ★</span></span>}
                  {r.sub_prazo && <span>Prazo: <span style={{ color: "var(--gold)" }}>{r.sub_prazo}/5 ★</span></span>}
                  {r.sub_comunicacao && <span>Comunicação: <span style={{ color: "var(--gold)" }}>{r.sub_comunicacao}/5 ★</span></span>}
                  {r.sub_instalacao && <span>Instalação: <span style={{ color: "var(--gold)" }}>{r.sub_instalacao}/5 ★</span></span>}
                </div>
              )}
              {r.comentario && (
                <div style={{ padding: "12px 14px", background: "var(--bg-3)", borderLeft: "2px solid var(--gold)", marginTop: 10, fontStyle: "italic", fontSize: 13, color: "var(--ink-2)" }}>
                  <MessageSquare size={13} color="var(--gold)" style={{ display: "inline", marginRight: 8, verticalAlign: "middle" }} />
                  "{r.comentario}"
                </div>
              )}
              {r.foto_url && (
                <div style={{ marginTop: 12, height: 140, backgroundImage: `url(${fileUrl(r.foto_url)})`, backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--gold-3)" }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* =========================================================================
 * OBRA NPS CARD — embedded in ObraDetail Visão Geral
 * ========================================================================= */
export function ObraNPSCard({ obraId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    authApi("get", `/obras/${obraId}/nps`).then(setData).catch(() => setData({ items: [] }));
  }, [obraId]);

  if (!data) return null;
  const items = data.items || [];
  const invite = data.invite;
  const hasResponse = items.length > 0;

  return (
    <div className="panel" style={{ padding: 20, marginBottom: 16 }} data-testid="obra-nps-card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div>
          <div className="eyebrow"><Award size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--gold)" }} />NPS pós-entrega</div>
        </div>
        {invite && (
          <div style={{ fontSize: 10, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".15em" }}>
            {invite.responded_at ? "Respondido" : invite.sent_at ? "Aguardando resposta" : "Pendente"}
          </div>
        )}
      </div>
      {!invite && (
        <p style={{ fontSize: 13, color: "var(--ink-3)", fontStyle: "italic" }}>
          Convite NPS será gerado automaticamente após a obra ter <strong>data de entrega real</strong> preenchida.
        </p>
      )}
      {invite && !hasResponse && (
        <p style={{ fontSize: 13, color: "var(--ink-2)" }}>
          Email enviado em <strong>{invite.sent_at ? new Date(invite.sent_at).toLocaleDateString("pt-BR") : "—"}</strong> para {invite.cliente_email}.
          {invite.reminder_sent_at && <> Lembrete enviado em {new Date(invite.reminder_sent_at).toLocaleDateString("pt-BR")}.</>}
        </p>
      )}
      {hasResponse && items.map(r => (
        <div key={r.id} style={{ marginTop: 6 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
            <span style={{ fontFamily: "'Fraunces',serif", fontSize: 36, fontStyle: "italic", color: "var(--gold)" }}>{r.nota_geral}/10</span>
            <span style={{ fontSize: 11, letterSpacing: ".2em", textTransform: "uppercase", color: r.categoria === "promoter" ? "var(--verde-2)" : r.categoria === "passive" ? "var(--gold)" : "#c47a7a" }}>
              {r.categoria}
            </span>
            <span style={{ fontSize: 11, color: "var(--ink-3)" }}>{new Date(r.responded_at).toLocaleDateString("pt-BR")}</span>
          </div>
          {r.comentario && (
            <p style={{ fontStyle: "italic", color: "var(--ink-2)", fontSize: 13, marginTop: 8, padding: "10px 14px", background: "var(--bg-3)", borderLeft: "2px solid var(--gold)" }}>
              "{r.comentario}"
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
